import { formatDate } from '@angular/common';
import { Component, computed, inject, input, OnInit, signal } from '@angular/core';
import { ColDef, GridOptions } from 'ag-grid-community';
import { finalize } from 'rxjs';
import { tryGetErrorMessage } from '../../../error.utils';
import { defaultTableDefOptions } from '../../../helpers';
import { AssetRecoveryService } from '../../../service/asset-recovery.service';
import { NotifyService } from '../../../service/notify.service';
import { UserLoginService } from '../../../service/user-login.service';
import { AssetRecoveryReport, AssetRecoveryReportIncident, ShareCasesRequest } from '../../../types';
import { CellAssetRecoveryActionsComponent } from '../../tabs/asset-recovery/cell-asset-recovery-actions/cell-asset-recovery-actions.component';

type AssetRecoveryReportTableRow = AssetRecoveryReportIncident & {
  asset_recovery_id: string;
};

@Component({
  selector: 'app-asset-recovery-report',
  templateUrl: './asset-recovery-report.component.html',
  standalone: false,
})
export class AssetRecoveryReportComponent implements OnInit {
  private assetRecoveryService = inject(AssetRecoveryService);
  private notifyService = inject(NotifyService);
  protected userLoginService = inject(UserLoginService);

  public assetRecoveryId = input.required<string>();
  public loading = signal(true);
  public isSharing = signal(false);
  public isInvoiceVisible = signal(false);
  public isInvoiceSaving = signal(false);
  public report = signal<AssetRecoveryReport | undefined>(undefined);

  public subtitle = computed(() => {
    const report = this.report();
    if (!report) return '';

    return `Created on ${formatDate(report.created_at, 'medium', 'en-US')} by ${report.created_by ?? ''}`;
  });

  public locationName = computed(() => this.report()?.location_name ?? '');
  public reportLocationIds = computed(() => {
    const locationId = this.report()?.location_id;
    return locationId ? [locationId] : [];
  });

  public canManageAssetRecovery = computed(() => {
    const report = this.report();
    return !!report && this.userLoginService.canManageAssetRecovery(report.branch_id.toString());
  });

  public reportRows = computed<AssetRecoveryReportTableRow[]>(() => {
    const report = this.report();
    if (!report) return [];

    return report.incidents.map((incident) => ({
      ...incident,
      asset_recovery_id: report.id,
    }));
  });

  public updateInvoice(addedFees: number) {
    const report = this.report();
    if (!report) return;

    this.isInvoiceSaving.set(true);
    this.assetRecoveryService
      .updateAssetRecoveryInvoice(report.id, addedFees)
      .pipe(finalize(() => this.isInvoiceSaving.set(false)))
      .subscribe({
        next: () => {
          this.report.update((current) => (current ? { ...current, added_fees: addedFees } : current));
          this.isInvoiceVisible.set(false);
          this.notifyService.notify('Asset Recovery invoice updated successfully.');
        },
        error: (error) => this.notifyService.notify('Failed to update Asset Recovery invoice: ' + tryGetErrorMessage(error)),
      });
  }

  public readonly tableDef: GridOptions<AssetRecoveryReportTableRow> = {
    ...defaultTableDefOptions(),
    pagination: true,
    paginationPageSize: 10,
    paginationPageSizeSelector: [10, 20, 50],
    onGridSizeChanged: (params) => params.api.sizeColumnsToFit(),
  };

  public readonly columnDefs: ColDef<AssetRecoveryReportTableRow>[] = [
    {
      field: 'case_index',
      headerName: 'Case #',
      filter: 'agNumberColumnFilter',
      sortable: true,
      flex: 1,
    },
    {
      field: 'asset_recovery_id',
      headerName: 'Asset Recovery ID',
      filter: 'agTextColumnFilter',
      sortable: true,
      flex: 1,
    },
    {
      field: 'incident_date',
      headerName: 'Incident Date',
      filter: 'agDateColumnFilter',
      sortable: true,
      flex: 1,
      valueFormatter: (params) => (params.value ? formatDate(params.value, 'MM/dd/yyyy', 'en-US') : ''),
    },
    {
      headerName: '',
      sortable: false,
      filter: false,
      suppressKeyboardEvent: () => true,
      maxWidth: 100,
      cellClass: ['inline-flex', 'items-center', 'justify-end'],
      cellRenderer: CellAssetRecoveryActionsComponent,
      cellRendererParams: {
        showReportButton: false,
      },
    },
  ];

  ngOnInit() {
    this.assetRecoveryService
      .getAssetRecoveryReportById(this.assetRecoveryId())
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: (report) => this.report.set(report),
        error: (error) => this.notifyService.notify('Failed to load asset recovery report: ' + tryGetErrorMessage(error)),
      });
  }

  share(shareData: Partial<ShareCasesRequest>) {
    this.isSharing.set(true);
    this.assetRecoveryService
      .shareAssetRecoveryReport(this.assetRecoveryId(), {
        emails: shareData.emails ?? [],
        message: shareData.message ?? '',
        to_guests: shareData.to_guests ?? false,
        cc_sender: shareData.cc_sender ?? false,
      })
      .pipe(finalize(() => this.isSharing.set(false)))
      .subscribe({
        next: () => {
          this.report.update((report) => (report ? { ...report, is_shared: true } : report));
          this.notifyService.notify('Report shared successfully.');
        },
        error: () => this.notifyService.notify('Failed to share report.'),
      });
  }
}
